import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { dropzone } from 'helpers/makeFormFields';
import { defaultToastOptions, manageErrorResponse } from 'helpers/manageErrorResponse';
import useFileUploadMutation from 'hooks/mutations/useFileUploadMutation';
import useUpdateLegalDocumentMutation from 'hooks/mutations/useUpdateLegalDocumentMutation';
import { useLegalDocumentQuery } from 'hooks/useLegalDocumentQuery';
import { useLegalDocumentsQuery } from 'hooks/useLegalDocumentsQuery';
import { ContentCard } from 'layout/ContentCard';
import PageMetatags from 'layout/PageMetatags';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

const UpdateLegalDocumentPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const legalDocumentId = Number(id);

  const legalDocumentQuery = useLegalDocumentQuery(legalDocumentId);
  const legalDocument = legalDocumentQuery.data?.data;

  const updateLegalDocumentVersions = useUpdateLegalDocumentMutation(legalDocumentId);
  const fileUploadMutation = useFileUploadMutation();

  const schema = yup
    .object({
      version: yup.string().required(),
      type: yup.string().oneOf(['dpp', 'tos']).required(),
      effective_at_month: yup.string().required(),
      effective_at_year: yup.number().required(),
      en_file: yup
        .object()
        .shape({
          file: yup.mixed<File>(),
        })
        .optional(),
      de_file: yup
        .object()
        .shape({
          file: yup.mixed<File>(),
        })
        .optional(),
      de_file_uuid: yup.string(),
      en_file_uuid: yup.string(),
    })
    .required();

  const {
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
    resetField,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      type: legalDocument?.type as 'tos' | 'dpp' | undefined,
      version: legalDocument?.version ?? '',
      effective_at_month: monthNumberToString(new Date().getMonth()),
      effective_at_year: new Date().getFullYear(),
    },
  });

  const enFile = watch('en_file');
  const deFile = watch('de_file');

  function monthNumberToString(month: number) {
    return (month + 1).toString().padStart(2, '0');
  }

  function handleEnFileChange(value?: { file: File | null; meta: { name: string; size: number; type: string } }) {
    if (value?.file != null) {
      const formData = new FormData();
      formData.append('file', value.file);
      fileUploadMutation.mutate(formData, {
        onSuccess: (fileData) => setValue('en_file_uuid', fileData.data.uuid),
        onError: (error: any) => {
          const { errors } = error.response.data;
          manageErrorResponse(toast, errors);
        },
      });
    } else {
      resetField('en_file_uuid');
    }
  }

  function handleDeFileChange(value?: { file: File | null; meta: { name: string; size: number; type: string } }) {
    if (value?.file != null) {
      const formData = new FormData();
      formData.append('file', value.file);
      fileUploadMutation.mutate(formData, {
        onSuccess: (fileData) => setValue('de_file_uuid', fileData.data.uuid),
        onError: (error: any) => {
          const { errors } = error.response.data;
          manageErrorResponse(toast, errors);
        },
      });
    } else {
      resetField('de_file_uuid');
    }
  }

  function onSubmit(form: yup.InferType<typeof schema>) {
    updateLegalDocumentVersions.mutate(
      {
        type: form.type,
        version: form.version,
        effective_at: `${form.effective_at_year}-${form.effective_at_month}`,
        de_file_uuid: form.de_file_uuid,
        en_file_uuid: form.en_file_uuid,
      },
      {
        onError: (error: any) => {
          toast({
            title: error.message,
            status: 'error',
            ...defaultToastOptions,
          });
        },
        onSuccess: async (updatedLegalDocument) => {
          queryClient.setQueriesData({ queryKey: useLegalDocumentQuery.getKey(legalDocumentId) }, updatedLegalDocument);
          await queryClient.invalidateQueries({ queryKey: useLegalDocumentsQuery.getKey() });
          navigate('/app/op/legal-documents');
          toast({
            title: 'Success!',
            status: 'success',
            ...defaultToastOptions,
          });
        },
      },
    );
  }

  useEffect(() => {
    if (legalDocumentQuery.isSuccess) {
      setValue('version', legalDocumentQuery.data.data!.version);
      setValue('type', legalDocumentQuery.data.data!.type as 'tos' | 'dpp');
      setValue('effective_at_month', monthNumberToString(new Date(legalDocumentQuery.data.data!.effective_at!).getMonth()));
      setValue('effective_at_year', new Date(legalDocumentQuery.data.data!.effective_at!).getFullYear());
    }
  }, [legalDocumentQuery.isSuccess, legalDocumentQuery.data, setValue]);

  if (isNaN(legalDocumentId)) {
    navigate('/404', { replace: true });
    return null;
  }

  return (
    <div>
      <PageMetatags title="Update legal document version" />
      <ContentCard header={t('legal_documents:update_legal_document.title')}>
        {legalDocumentQuery.isLoading && <LoadingSpinner />}
        {legalDocumentQuery.isSuccess && (
          <Stack as="form" id="update-legal-documents" spacing={8} p={1} onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="type"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl isInvalid={fieldState.invalid}>
                  <FormLabel>{t('legal_documents:legal_document_properties.types.placeholder')}</FormLabel>
                  <Select placeholder={t('legal_documents:legal_document_properties.types.placeholder')} {...field}>
                    <option value="tos">{t('legal_documents:legal_document_properties.types.tos')}</option>
                    <option value="dpp">{t('legal_documents:legal_document_properties.types.dpp')}</option>
                  </Select>
                  <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
                </FormControl>
              )}
            />

            <Controller
              name="version"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl isInvalid={fieldState.invalid}>
                  <FormLabel>{t('legal_documents:legal_document_properties.version')}</FormLabel>
                  <Input placeholder="1.0" {...field} />
                  <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
                </FormControl>
              )}
            />

            <Controller
              name="effective_at_month"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl isInvalid={fieldState.invalid}>
                  <FormLabel>{t('legal_documents:month_dropdown.month')}</FormLabel>
                  <Select disabled={legalDocument?.status != 'planned'} placeholder={t('legal_documents:month_dropdown.placeholder')} {...field}>
                    <option value="01">{t('legal_documents:month_dropdown.jan')}</option>
                    <option value="02">{t('legal_documents:month_dropdown.feb')}</option>
                    <option value="03">{t('legal_documents:month_dropdown.mar')}</option>
                    <option value="04">{t('legal_documents:month_dropdown.apr')}</option>
                    <option value="05">{t('legal_documents:month_dropdown.may')}</option>
                    <option value="06">{t('legal_documents:month_dropdown.jun')}</option>
                    <option value="07">{t('legal_documents:month_dropdown.jul')}</option>
                    <option value="08">{t('legal_documents:month_dropdown.aug')}</option>
                    <option value="09">{t('legal_documents:month_dropdown.sep')}</option>
                    <option value="10">{t('legal_documents:month_dropdown.oct')}</option>
                    <option value="11">{t('legal_documents:month_dropdown.nov')}</option>
                    <option value="12">{t('legal_documents:month_dropdown.dec')}</option>
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              name="effective_at_year"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl isInvalid={fieldState.invalid}>
                  <FormLabel>{t('legal_documents:year')}</FormLabel>
                  <Input disabled={legalDocument?.status != 'planned'} placeholder="Enter year" {...field} />
                </FormControl>
              )}
            />
            {dropzone({
              name: 'en_file',
              label: (
                <Box>
                  <FormLabel>{t('legal_documents:legal_document_properties.versions.en')}</FormLabel>

                  {(enFile?.file?.name != null || legalDocument?.en_file_name != null) && (
                    <Text>
                      {t('legal_documents:selected_file.label', {
                        filename: enFile?.file?.name ?? legalDocument?.en_file_name,
                      })}
                    </Text>
                  )}
                </Box>
              ),
              control,
              errors,
              onChange: handleEnFileChange,
            })}
            {dropzone({
              name: 'de_file',
              label: (
                <Box>
                  <FormLabel>{t('legal_documents:legal_document_properties.versions.de')}</FormLabel>

                  {(deFile?.file?.name != null || legalDocument?.de_file_name != null) && (
                    <Text>
                      {t('legal_documents:selected_file.label', {
                        filename: deFile?.file?.name ?? legalDocument?.de_file_name,
                      })}
                    </Text>
                  )}
                </Box>
              ),
              control,
              errors,
              onChange: handleDeFileChange,
            })}
          </Stack>
        )}

        <Flex justifyContent="flex-end" gap="2">
          <Button as={Link} to={`/app/op/legal-document-version/${id}`}>
            {t('legal_documents:update_legal_document.button_cancel')}
          </Button>
          {legalDocument?.status === 'enrolling' || legalDocument?.status === 'enrolled' ? (
            <>
              <Button variant="primary" onClick={onOpen}>
                {t('legal_documents:update_legal_document.button_save')}
              </Button>

              <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader>{t('legal_documents:update_legal_document.modal.title')}</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>{t('legal_documents:update_legal_document.modal.body')}</ModalBody>
                  <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>
                      {t('legal_documents:update_legal_document.modal.button_cancel')}
                    </Button>
                    <Button variant="primary" type="submit" form="update-legal-documents">
                      {t('legal_documents:update_legal_document.modal.button_cta')}
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
            </>
          ) : (
            <Button variant="primary" type="submit" form="update-legal-documents">
              {t('legal_documents:update_legal_document.button_save')}
            </Button>
          )}
        </Flex>
      </ContentCard>
    </div>
  );
};

export default UpdateLegalDocumentPage;
